/** Tenant roles ordered by authority (higher rank = more privilege). */
import { checkUserPermission, resolveMasterActiveModules } from './rolePermissions';

export const ROLE_RANK = {
  super_admin: 100,
  company_admin: 50,
  developer: 40,
  manager: 30,
  agent: 20,
  trainee: 10,
};

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  company_admin: 'Company Admin',
  developer: 'Developer',
  manager: 'Manager',
  agent: 'Agent',
  trainee: 'Trainee',
};

export const STAFF_ROLES = ['agent', 'manager', 'trainee'];

/** Roles that get the Users management section in the tenant dashboard. */
export const USERS_SECTION_ROLES = new Set(['company_admin', 'developer', 'manager']);

/** Roles that can open AI Studio in the sidebar. */
export const AI_STUDIO_ROLES = new Set(['company_admin', 'developer', 'super_admin']);

/** Roles that can access enterprise Reports (BigQuery builder). */
export const REPORTS_ROLES = new Set(['company_admin', 'manager', 'super_admin']);

/** Roles eligible for live chat session alerts and the Chat Operations dashboard. */
export const CHAT_OPS_ROLES = new Set(['agent', 'manager', 'company_admin', 'super_admin']);

/**
 * @param {string | null | undefined} role
 * @returns {number}
 */
export function roleRank(role) {
  return ROLE_RANK[role] ?? 0;
}

/**
 * @param {string | null | undefined} actorRole
 * @param {string | null | undefined} targetRole
 * @returns {boolean}
 */
export function isStrictlyBelow(actorRole, targetRole) {
  return roleRank(actorRole) > roleRank(targetRole);
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canAccessUsersSection(role) {
  return typeof role === 'string' && USERS_SECTION_ROLES.has(role);
}

/**
 * @param {string | null | undefined} role
 * @param {{ activeModules?: Record<string, boolean>, enabledModules?: Record<string, boolean> } | null | undefined} [workspace]
 * @returns {boolean}
 */
export function canAccessAiStudio(role, workspace) {
  if (typeof role !== 'string' || !AI_STUDIO_ROLES.has(role)) return false;
  if (role === 'super_admin') return true;
  if (!workspace) return true;
  return resolveMasterActiveModules(workspace).aiStudio === true;
}

/** Roles that can open AI Game Studio in the sidebar. */
export const GAME_STUDIO_ROLES = AI_STUDIO_ROLES;

/** Files module uses the same studio role gate as Game Studio / Image Editor. */
export const FILES_ROLES = GAME_STUDIO_ROLES;

/**
 * @param {string | null | undefined} role
 * @param {{ activeModules?: Record<string, boolean>, enabledModules?: Record<string, boolean> } | null | undefined} [workspace]
 * @returns {boolean}
 */
export function canAccessGameStudio(role, workspace) {
  if (typeof role !== 'string' || !GAME_STUDIO_ROLES.has(role)) return false;
  if (role === 'super_admin') return true;
  if (!workspace) return true;
  return resolveMasterActiveModules(workspace).aiGameStudio === true;
}

/**
 * @param {string | null | undefined} role
 * @param {{ activeModules?: Record<string, boolean>, enabledModules?: Record<string, boolean> } | null | undefined} [workspace]
 * @returns {boolean}
 */
export function canAccessFiles(role, workspace) {
  if (typeof role !== 'string' || !FILES_ROLES.has(role)) return false;
  if (role === 'super_admin') return true;
  if (!workspace) return true;
  return resolveMasterActiveModules(workspace).files === true;
}

/**
 * @param {string | null | undefined} role
 * @param {{ activeModules?: Record<string, boolean>, enabledModules?: Record<string, boolean> } | null | undefined} [workspace]
 * @returns {boolean}
 */
export function canAccessReports(role, workspace) {
  if (typeof role !== 'string' || !REPORTS_ROLES.has(role)) return false;
  if (role === 'super_admin') return true;
  if (!workspace) return true;
  return resolveMasterActiveModules(workspace).reports === true;
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canAccessChatOperations(role) {
  return typeof role === 'string' && CHAT_OPS_ROLES.has(role);
}

/** @deprecated Prefer checkUserPermission(role, workspace, 'codeReview', 'read'). */
export const CODE_SANDBOX_REVIEW_ROLES = new Set(['super_admin', 'company_admin', 'developer']);

/**
 * @param {string | null | undefined} role
 * @param {{ activeModules?: Record<string, boolean>, rolePermissions?: Record<string, Record<string, string[]>> } | null | undefined} [workspace]
 * @returns {boolean}
 */
export function canAccessCodeSandboxReview(role, workspace) {
  try {
    return checkUserPermission(role, workspace, 'codeReview', 'read');
  } catch (err) {
    console.error('canAccessCodeSandboxReview:', err);
    return false;
  }
}

/**
 * @param {string | null | undefined} role
 * @param {{ activeModules?: Record<string, boolean>, rolePermissions?: Record<string, Record<string, string[]>> } | null | undefined} [workspace]
 * @returns {boolean}
 */
export function canManageCodeSandboxReview(role, workspace) {
  try {
    return checkUserPermission(role, workspace, 'codeReview', 'write');
  } catch (err) {
    console.error('canManageCodeSandboxReview:', err);
    return false;
  }
}

/**
 * Roles an actor may assign when creating or updating users.
 * @param {string | null | undefined} actorRole
 * @returns {string[]}
 */
export function getAssignableRoles(actorRole) {
  switch (actorRole) {
    case 'super_admin':
      return ['company_admin', 'developer', 'manager', 'agent', 'trainee'];
    case 'company_admin':
      return ['developer', 'manager', 'agent', 'trainee'];
    case 'developer':
      return ['manager', 'agent', 'trainee'];
    case 'manager':
      return ['agent', 'trainee'];
    default:
      return [];
  }
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canCreateUsers(role) {
  return getAssignableRoles(role).length > 0;
}

/**
 * @param {string | null | undefined} actorRole
 * @param {string | null | undefined} targetRole
 * @returns {boolean}
 */
export function canManageTargetUser(actorRole, targetRole) {
  if (!actorRole || !targetRole) return false;
  if (targetRole === 'super_admin') return false;
  return isStrictlyBelow(actorRole, targetRole);
}

/**
 * @param {string | null | undefined} actorRole
 * @param {string | null | undefined} targetRole
 * @param {string | null | undefined} newRole
 * @returns {boolean}
 */
export function canAssignUserRole(actorRole, targetRole, newRole) {
  if (!canManageTargetUser(actorRole, targetRole)) return false;
  if (targetRole === newRole) return true;
  if (!getAssignableRoles(actorRole).includes(newRole)) return false;
  return isStrictlyBelow(actorRole, newRole);
}

/**
 * Which roles in the granular RBAC matrix an actor may edit.
 * @param {string | null | undefined} actorRole
 * @returns {string[]}
 */
export function getEditablePermissionRoles(actorRole) {
  if (actorRole === 'company_admin' || actorRole === 'super_admin') {
    return ['manager', 'developer', 'agent'];
  }
  if (actorRole === 'developer') {
    return ['manager', 'agent'];
  }
  if (actorRole === 'manager') {
    return ['agent'];
  }
  return [];
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canEditRolePermissions(role) {
  return getEditablePermissionRoles(role).length > 0;
}

/** Roles allowed to use the ticket rich-text editor (when preference is on). */
export const RICH_TEXT_ROLES = ['super_admin', 'company_admin', 'developer'];

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canUseRichText(role) {
  return RICH_TEXT_ROLES.includes(role);
}
